import './BrandingPreview.css';

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BusinessSettingsLayout } from '@components/layout/BusinessSettingsLayout';
import { Button } from '@components/ui/Button';
import { cn } from '@/styles/tailwind-utils';
import { useAuthStore, selectActiveAccount } from '@features/auth/store/authStore';

// ─── Theme Registry ───────────────────────────────────────────────────────────

const THEMES = [
  { id: 'default',    name: 'Sage Calm',        primary: '160 35% 32%', bg: '60 20% 98%'  },
  { id: 'ocean',      name: 'Ocean Trust',      primary: '210 80% 38%', bg: '200 40% 98%' },
  { id: 'sunset',     name: 'Sunset Warmth',    primary: '15 75% 50%',  bg: '30 50% 97%'  },
  { id: 'midnight',   name: 'Midnight Pro',     primary: '250 80% 65%', bg: '230 25% 8%'  },
  { id: 'rose',       name: 'Rose Gold',        primary: '345 60% 50%', bg: '350 40% 98%' },
  { id: 'forest',     name: 'Forest Wellness',  primary: '140 45% 25%', bg: '90 25% 96%'  },
  { id: 'mono',       name: 'Minimal Mono',     primary: '0 0% 10%',    bg: '0 0% 100%'   },
  { id: 'lavender',   name: 'Lavender Calm',    primary: '265 55% 55%', bg: '270 40% 98%' },
  { id: 'clinical',   name: 'Clinical Blue',    primary: '195 75% 42%', bg: '210 30% 98%' },
  { id: 'terracotta', name: 'Terracotta Earth', primary: '15 55% 45%',  bg: '35 35% 96%'  },
] as const;

type ThemeId = typeof THEMES[number]['id'];

// ─── Preview Data ─────────────────────────────────────────────────────────────

const SERVICES = [
  { icon: '⬡', name: 'Physiotherapy',       desc: 'Personalized rehabilitation programs to restore movement and reduce pain.' },
  { icon: '◈', name: 'Manual Therapy',       desc: 'Hands-on techniques to relieve tension, improve mobility, and aid recovery.' },
  { icon: '◇', name: 'Sports Rehabilitation', desc: 'Tailored programs for athletes to recover fast and return to peak performance.' },
] as const;

const PRACTITIONERS = [
  { name: 'Dr. Sarah Ionescu',    role: 'Senior Physiotherapist',    initials: 'SI' },
  { name: 'Alex Marinescu, PT',   role: 'Manual Therapy Specialist', initials: 'AM' },
  { name: 'Dr. Mihai Constantin', role: 'Sports Medicine',           initials: 'MC' },
  { name: 'Elena Radu, MSc',      role: 'Rehabilitation Expert',     initials: 'ER' },
] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

interface BrandingConfig {
  theme: ThemeId;
  title: string;
  subtitle: string;
  tagline: string;
  photoUrl: string;
}

const DEFAULT_CONFIG: BrandingConfig = {
  theme: 'default',
  title: 'Move better.',
  subtitle: 'Live stronger.',
  tagline: 'Personalized physiotherapy plans built around your body, your pain, and your goals — delivered by clinicians who actually listen.',
  photoUrl: '/hero-physio.jpg',
};

// ─── Clinic Page Preview ──────────────────────────────────────────────────────

function ClinicPagePreview({ config, businessName }: { config: BrandingConfig; businessName: string }) {
  const v = (name: string) => `hsl(var(--${name}))`;

  return (
    <div
      className="branding-preview"
      data-theme={config.theme !== 'default' ? config.theme : undefined}
      style={{ fontFamily: 'var(--font-body)', background: v('background'), color: v('foreground') }}
    >
      {/* ── Navbar ─────────────────────────────────────────────── */}
      <header
        style={{
          background: v('background'),
          borderBottom: `1px solid ${v('border')}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 2rem',
            display: 'flex',
            alignItems: 'center',
            height: '64px',
            gap: '2rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: '1.25rem',
              color: v('foreground'),
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: '30px',
                height: '30px',
                background: v('primary'),
                borderRadius: 'calc(var(--radius) * 0.5)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: v('primary-foreground'),
                fontSize: '13px',
                fontWeight: 900,
                letterSpacing: '-0.5px',
                textTransform: 'uppercase',
              }}
            >
              {businessName.charAt(0)}
            </span>
            {businessName}
          </div>

          <nav style={{ display: 'flex', gap: '1.75rem' }}>
            {['Services', 'Practitioners'].map((item) => (
              <a
                key={item}
                href="#"
                onClick={(e) => e.preventDefault()}
                style={{ color: v('muted-foreground'), fontSize: '0.875rem', textDecoration: 'none', fontWeight: 500 }}
              >
                {item}
              </a>
            ))}
          </nav>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              style={{ color: v('muted-foreground'), fontSize: '0.875rem', textDecoration: 'none', fontWeight: 500 }}
            >
              Login
            </a>
            <button
              style={{
                background: v('primary'),
                color: v('primary-foreground'),
                border: 'none',
                borderRadius: 'calc(var(--radius) * 0.75)',
                padding: '0.5rem 1.125rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'default',
                letterSpacing: '0.01em',
              }}
            >
              Book now
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '5rem 2rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '5rem',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '3.75rem',
                fontWeight: 700,
                lineHeight: 1.08,
                color: v('foreground'),
                margin: 0,
              }}
            >
              {config.title}
            </h1>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '3.75rem',
                fontWeight: 700,
                lineHeight: 1.08,
                color: v('primary'),
                fontStyle: 'italic',
                margin: 0,
              }}
            >
              {config.subtitle}
            </h1>
          </div>

          <p style={{ color: v('muted-foreground'), fontSize: '1.0625rem', lineHeight: 1.65, margin: 0 }}>
            {config.tagline}
          </p>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              style={{
                background: v('primary'),
                color: v('primary-foreground'),
                border: 'none',
                borderRadius: 'var(--radius)',
                padding: '0.8125rem 1.625rem',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: 'default',
              }}
            >
              Book a practitioner
            </button>
            <button
              style={{
                background: 'transparent',
                color: v('foreground'),
                border: `1.5px solid ${v('border')}`,
                borderRadius: 'var(--radius)',
                padding: '0.8125rem 1.625rem',
                fontSize: '0.9375rem',
                fontWeight: 600,
                cursor: 'default',
              }}
            >
              Explore services
            </button>
          </div>
        </div>

        <div
          style={{
            borderRadius: 'var(--radius)',
            overflow: 'hidden',
            aspectRatio: '4/3',
            boxShadow: '0 24px 64px -16px rgba(0,0,0,0.18)',
          }}
        >
          <img
            src={config.photoUrl}
            alt="Clinic"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      </section>

      {/* ── Services ───────────────────────────────────────────── */}
      <section style={{ background: v('muted'), padding: '5rem 2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{ color: v('primary'), fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center', margin: '0 0 0.5rem' }}>
            What we offer
          </p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.25rem', fontWeight: 700, color: v('foreground'), textAlign: 'center', margin: '0 0 0.75rem' }}>
            Our Services
          </h2>
          <p style={{ color: v('muted-foreground'), textAlign: 'center', maxWidth: '480px', margin: '0 auto 3rem', fontSize: '1rem', lineHeight: 1.6 }}>
            Evidence-based treatments tailored to your recovery goals.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
            {SERVICES.map((service) => (
              <div key={service.name} style={{ background: v('card'), border: `1px solid ${v('border')}`, borderRadius: 'var(--radius)', padding: '2rem' }}>
                <div style={{ width: '44px', height: '44px', background: v('secondary'), borderRadius: 'calc(var(--radius) * 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem', color: v('primary'), fontSize: '1.375rem' }}>
                  {service.icon}
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.125rem', fontWeight: 700, color: v('foreground'), margin: '0 0 0.5rem' }}>
                  {service.name}
                </h3>
                <p style={{ color: v('muted-foreground'), fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 1.25rem' }}>
                  {service.desc}
                </p>
                <a href="#" onClick={(e) => e.preventDefault()} style={{ color: v('primary'), fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none' }}>
                  Learn more →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Practitioners ──────────────────────────────────────── */}
      <section style={{ padding: '5rem 2rem', background: v('background') }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{ color: v('primary'), fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center', margin: '0 0 0.5rem' }}>
            Our team
          </p>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.25rem', fontWeight: 700, color: v('foreground'), textAlign: 'center', margin: '0 0 0.75rem' }}>
            Meet Our Practitioners
          </h2>
          <p style={{ color: v('muted-foreground'), textAlign: 'center', maxWidth: '480px', margin: '0 auto 3rem', fontSize: '1rem', lineHeight: 1.6 }}>
            Experienced clinicians dedicated to your full recovery.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
            {PRACTITIONERS.map((p) => (
              <div key={p.name} style={{ textAlign: 'center', background: v('card'), border: `1px solid ${v('border')}`, borderRadius: 'var(--radius)', padding: '2rem 1.25rem' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: v('secondary'), color: v('primary'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.125rem', fontWeight: 700, margin: '0 auto 1.25rem', fontFamily: 'var(--font-display)' }}>
                  {p.initials}
                </div>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: v('foreground'), margin: '0 0 0.3rem' }}>
                  {p.name}
                </h3>
                <p style={{ fontSize: '0.8125rem', color: v('muted-foreground'), margin: '0 0 1.25rem' }}>
                  {p.role}
                </p>
                <button style={{ background: 'transparent', border: `1px solid ${v('primary')}`, color: v('primary'), borderRadius: 'calc(var(--radius) * 0.75)', padding: '0.4375rem 0.875rem', fontSize: '0.8125rem', fontWeight: 600, cursor: 'default' }}>
                  View profile
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Edit Drawer ──────────────────────────────────────────────────────────────

interface EditDrawerProps {
  isOpen: boolean;
  draft: BrandingConfig;
  isDirty: boolean;
  onChange: (patch: Partial<BrandingConfig>) => void;
  onSave: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

function EditDrawer({ isOpen, draft, isDirty, onChange, onSave, onDiscard, onClose }: EditDrawerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    onChange({ photoUrl: URL.createObjectURL(file) });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit page"
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-[440px] flex-col bg-background shadow-2xl transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-base font-semibold text-foreground">Edit page</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-md text-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-7">

            {/* ── Theme ──────────────────────────────────────────── */}
            <section>
              <p className="mb-3 text-sm font-medium text-foreground">Theme</p>
              <div className="grid grid-cols-5 gap-2">
                {THEMES.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => onChange({ theme: theme.id })}
                    className={cn(
                      'flex flex-col items-start rounded-lg border-2 p-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                      draft.theme === theme.id
                        ? 'border-primary ring-2 ring-primary ring-offset-1'
                        : 'border-border hover:border-muted-foreground/40',
                    )}
                  >
                    <div className="mb-1.5 flex h-8 w-full overflow-hidden rounded-md">
                      <div className="flex-1" style={{ background: `hsl(${theme.bg})` }} />
                      <div style={{ width: '38%', background: `hsl(${theme.primary})` }} />
                    </div>
                    <span className="text-[10px] font-medium leading-tight text-foreground">
                      {theme.name}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            {/* ── Title ──────────────────────────────────────────── */}
            <section>
              <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="bp-title">
                Title
              </label>
              <input
                id="bp-title"
                type="text"
                value={draft.title}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="e.g. Move better."
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:ring-offset-1"
              />
            </section>

            {/* ── Subtitle ───────────────────────────────────────── */}
            <section>
              <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="bp-subtitle">
                Subtitle
              </label>
              <input
                id="bp-subtitle"
                type="text"
                value={draft.subtitle}
                onChange={(e) => onChange({ subtitle: e.target.value })}
                placeholder="e.g. Live stronger."
                className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:ring-offset-1"
              />
            </section>

            {/* ── Tagline ────────────────────────────────────────── */}
            <section>
              <label className="mb-1.5 block text-sm font-medium text-foreground" htmlFor="bp-tagline">
                Tagline
              </label>
              <textarea
                id="bp-tagline"
                rows={3}
                value={draft.tagline}
                onChange={(e) => onChange({ tagline: e.target.value })}
                placeholder="Describe your clinic in one or two sentences…"
                className="w-full resize-none rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:ring-offset-1"
              />
            </section>

            {/* ── Business photo ─────────────────────────────────── */}
            <section>
              <p className="mb-1.5 text-sm font-medium text-foreground">Business photo</p>
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files[0];
                  if (file) handleFile(file);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'group relative aspect-[4/3] w-full cursor-pointer overflow-hidden rounded-xl border-2 transition-all',
                  isDragging ? 'border-primary' : 'border-border hover:border-muted-foreground/40',
                )}
              >
                <img
                  src={draft.photoUrl}
                  alt="Business"
                  className="h-full w-full object-cover"
                />

                {/* Hover / drag overlay */}
                <div
                  className={cn(
                    'absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-black/55 transition-opacity',
                    isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                  )}
                >
                  <span className="text-3xl">📷</span>
                  <p className="text-sm font-medium text-white">
                    {isDragging ? 'Drop to upload' : 'Click or drop to replace'}
                  </p>
                </div>

                {/* Drag-over border ring */}
                {isDragging && (
                  <div className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-inset ring-primary" />
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = '';
                }}
              />

              <p className="mt-1.5 text-xs text-muted-foreground">
                JPG, PNG or WebP · Recommended 4:3 ratio
              </p>
            </section>

          </div>
        </div>

        {/* Footer actions */}
        <div className="flex shrink-0 gap-3 border-t border-border px-6 py-4">
          <Button
            variant="secondary"
            size="md"
            disabled={!isDirty}
            onClick={onDiscard}
            className="flex-1"
          >
            Discard changes
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!isDirty}
            onClick={onSave}
            className="flex-1"
          >
            Save changes
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Branding Page ────────────────────────────────────────────────────────────

export function BrandingPage() {
  const { t } = useTranslation();
  const activeAccount = useAuthStore(selectActiveAccount);
  const businessName = activeAccount?.businessName ?? '';

  const [saved, setSaved] = useState<BrandingConfig>(DEFAULT_CONFIG);
  const [draft, setDraft] = useState<BrandingConfig>(DEFAULT_CONFIG);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const blobUrlsRef = useRef<string[]>([]);
  useEffect(() => () => { blobUrlsRef.current.forEach(URL.revokeObjectURL); }, []);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const handleChange = useCallback((patch: Partial<BrandingConfig>) => {
    if (patch.photoUrl?.startsWith('blob:')) blobUrlsRef.current.push(patch.photoUrl);
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const handleSave = useCallback(() => {
    setSaved((prev) => ({ ...prev, ...draft }));
    setIsDrawerOpen(false);
  }, [draft]);

  const handleDiscard = useCallback(() => {
    setDraft(saved);
  }, [saved]);

  return (
    <BusinessSettingsLayout>
      <div className="flex flex-col gap-10">

        {/* ── Preview ────────────────────────────────────────────── */}
        <section>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="text-base font-semibold text-foreground">
                {t('settings.branding.previewSection')}
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {t('settings.branding.previewDesc')}
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setIsDrawerOpen(true)}>
              {t('settings.branding.edit')}
            </Button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border shadow-sm">
            <ClinicPagePreview config={draft} businessName={businessName} />
          </div>
        </section>

      </div>

      <EditDrawer
        isOpen={isDrawerOpen}
        draft={draft}
        isDirty={isDirty}
        onChange={handleChange}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onClose={() => setIsDrawerOpen(false)}
      />
    </BusinessSettingsLayout>
  );
}
